import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";
import { getOrderCounts } from "../../api/admin";
import { getAtelierOrderCounts } from "../../api/atelier";
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  BarChart2,
  Tag,
  Scissors,
  Megaphone,
  Settings,
  Image as ImageIcon,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import useAuth from "../../hooks/useAuth";
import logo from "../../assets/images/777_logo.webp";
import {
  Layout,
  Sidebar,
  SidebarOverlay,
  SidebarBrand,
  SidebarNav,
  NavItem,
  SidebarLink,
  Tooltip,
  SidebarBottom,
  CollapseBtn,
  LogoutBtn,
  Main,
  TopBar,
  TopLeft,
  Hamburger,
  PageTitle,
  Content,
  AlertDot,
} from "./AdminLayout.styles";

const NAV_ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart2 },
  { to: "/admin/promo-codes", label: "Promo Codes", icon: Tag },
  { to: "/admin/atelier", label: "Atelier", icon: Scissors },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/admin/marketing", label: "Marketing", icon: Megaphone },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const PAGE_TITLES = {
  "/admin": "Dashboard",
  "/admin/products": "Products",
  "/admin/orders": "Orders",
  "/admin/analytics": "Analytics",
  "/admin/promo-codes": "Promo Codes",
  "/admin/atelier": "Atelier",
  "/admin/gallery": "Gallery",
  "/admin/marketing": "Marketing",
  "/admin/settings": "Settings",
};

const LS_KEY = "adminSidebarCollapsed";

export default function AdminLayout({ children }) {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  useLockBodyScroll(drawerOpen);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(LS_KEY) === "true";
    } catch {
      return false;
    }
  });

  const [pendingOrders, setPendingOrders] = useState(false);
  const [pendingAtelier, setPendingMia] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    const fetchCounts = () => {
      getOrderCounts()
        .then((data) => setPendingOrders((data?.PENDING || 0) > 0))
        .catch(() => {});
      getAtelierOrderCounts()
        .then((data) => setPendingMia((data?.PENDING || 0) > 0))
        .catch(() => {});
    };
    fetchCounts();
    pollRef.current = setInterval(fetchCounts, 60000);
    return () => clearInterval(pollRef.current);
  }, []);

  const [tooltip, setTooltip] = useState({
    visible: false,
    label: "",
    top: 0,
    left: 0,
  });

  const showTooltip = useCallback((e, label) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      label,
      top: rect.top + rect.height / 2,
      left: rect.right + 10,
    });
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  // Only show tooltips when sidebar is collapsed (or on tablet where it's always collapsed)
  const shouldShowTooltip =
    collapsed ||
    (typeof window !== "undefined" &&
      window.innerWidth <= 1024 &&
      window.innerWidth > 768);

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LS_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const pageTitle = PAGE_TITLES[location.pathname] || "Admin";

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <Layout>
      <SidebarOverlay $open={drawerOpen} onClick={() => setDrawerOpen(false)} />
      <Sidebar $open={drawerOpen} $collapsed={collapsed}>
        <CollapseBtn onClick={toggleCollapse}>
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </CollapseBtn>
        <SidebarBrand $collapsed={collapsed}>
          <img src={logo} alt="777" />
          <span>ADMIN</span>
        </SidebarBrand>
        <SidebarNav>
          {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => {
            const showDot =
              (to === "/admin/orders" && pendingOrders) ||
              (to === "/admin/atelier" && pendingAtelier);
            return (
              <NavItem
                key={to}
                onMouseEnter={(e) => shouldShowTooltip && showTooltip(e, label)}
                onMouseLeave={hideTooltip}
              >
                <SidebarLink
                  to={to}
                  end={end}
                  $collapsed={collapsed}
                  onClick={() => setDrawerOpen(false)}
                >
                  <Icon />
                  <span>{label}</span>
                </SidebarLink>
                {showDot && <AlertDot />}
              </NavItem>
            );
          })}
        </SidebarNav>
        <SidebarBottom>
          <NavItem
            onMouseEnter={(e) => shouldShowTooltip && showTooltip(e, "Logout")}
            onMouseLeave={hideTooltip}
          >
            <LogoutBtn onClick={handleLogout} $collapsed={collapsed}>
              <LogOut />
              <span>Logout</span>
            </LogoutBtn>
          </NavItem>
        </SidebarBottom>
      </Sidebar>

      <Tooltip
        $visible={tooltip.visible}
        style={{ top: tooltip.top, left: tooltip.left }}
      >
        {tooltip.label}
      </Tooltip>

      <Main $collapsed={collapsed}>
        <TopBar>
          <TopLeft>
            <Hamburger onClick={() => setDrawerOpen(!drawerOpen)}>
              {drawerOpen ? <X size={22} /> : <Menu size={22} />}
            </Hamburger>
            <PageTitle>{pageTitle}</PageTitle>
          </TopLeft>
        </TopBar>
        <Content>{children}</Content>
      </Main>
    </Layout>
  );
}
